import { _ } from "libs";

/// <reference types="googlemaps" />
import React, { Component } from "react";
import ReactDOM from "react-dom";
import { SearchResult } from "../types";
import moment from "moment-timezone";
import MarkerClusterer, { ClusterIconInfo } from "@google/markerclustererplus";

type Props = {
	center: any;
	results: SearchResult[];
	timestamp: number;
	onMapMove: Function;
};

const mapStyles = [
	{
		elementType: "geometry",
		stylers: [
			{
				color: "#f5f5f5",
			},
		],
	},
	{
		elementType: "labels.icon",
		stylers: [
			{
				visibility: "off",
			},
		],
	},
	{
		elementType: "labels.text.fill",
		stylers: [
			{
				color: "#616161",
			},
		],
	},
	{
		elementType: "labels.text.stroke",
		stylers: [
			{
				color: "#f5f5f5",
			},
		],
	},
	{
		featureType: "administrative.land_parcel",
		elementType: "labels",
		stylers: [
			{
				visibility: "off",
			},
		],
	},
	{
		featureType: "administrative.land_parcel",
		elementType: "labels.text.fill",
		stylers: [
			{
				color: "#bdbdbd",
			},
		],
	},
	{
		featureType: "poi",
		elementType: "geometry",
		stylers: [
			{
				color: "#eeeeee",
			},
		],
	},
	{
		featureType: "poi",
		elementType: "labels.text",
		stylers: [
			{
				visibility: "off",
			},
		],
	},
	{
		featureType: "poi",
		elementType: "labels.text.fill",
		stylers: [
			{
				color: "#757575",
			},
		],
	},
	{
		featureType: "poi.business",
		stylers: [
			{
				visibility: "off",
			},
		],
	},
	{
		featureType: "poi.park",
		elementType: "geometry",
		stylers: [
			{
				color: "#e5e5e5",
			},
		],
	},
	{
		featureType: "poi.park",
		elementType: "labels.text",
		stylers: [
			{
				visibility: "off",
			},
		],
	},
	{
		featureType: "poi.park",
		elementType: "labels.text.fill",
		stylers: [
			{
				color: "#9e9e9e",
			},
		],
	},
	{
		featureType: "road",
		elementType: "geometry",
		stylers: [
			{
				color: "#ffffff",
			},
		],
	},
	{
		featureType: "road.arterial",
		elementType: "labels.text.fill",
		stylers: [
			{
				color: "#757575",
			},
		],
	},
	{
		featureType: "road.highway",
		elementType: "geometry",
		stylers: [
			{
				color: "#dadada",
			},
		],
	},
	{
		featureType: "road.highway",
		elementType: "labels.text.fill",
		stylers: [
			{
				color: "#616161",
			},
		],
	},
	{
		featureType: "road.local",
		elementType: "labels",
		stylers: [
			{
				visibility: "off",
			},
		],
	},
	{
		featureType: "road.local",
		elementType: "labels.text.fill",
		stylers: [
			{
				color: "#9e9e9e",
			},
		],
	},
	{
		featureType: "transit.line",
		elementType: "geometry",
		stylers: [
			{
				color: "#e5e5e5",
			},
		],
	},
	{
		featureType: "transit.station",
		elementType: "geometry",
		stylers: [
			{
				color: "#eeeeee",
			},
		],
	},
	{
		featureType: "water",
		elementType: "geometry",
		stylers: [
			{
				color: "#c9c9c9",
			},
		],
	},
	{
		featureType: "water",
		elementType: "labels.text.fill",
		stylers: [
			{
				color: "#9e9e9e",
			},
		],
	},
] as google.maps.MapTypeStyle[];

const defaultClusterStyle = {
	textSize: 12,
	textColor: "#99A9AD", //"#FF5524",
	width: 40,
	height: 40,
	url: process.env.PUBLIC_URL + "/icon_40x40_cluster.svg",
};

const getClusterStyle = (markers: google.maps.Marker[]): number => {
	let counter = { now: 0, later: 0, earlier: 0 };
	let style = 0;

	for (let marker of markers) {
		if (marker["className"] === "now") {
			counter.now += 1;
		}

		if (marker["className"] === "later") {
			counter.later += 1;
		}

		if (marker["className"] === "earlier") {
			counter.earlier += 1;
		}
	}

	if (counter.now > 0) {
		style = 1;
	}

	if (counter.later > 0) {
		style = 2;
	}

	if (counter.earlier > 0) {
		style = 3;
	}

	return style;
};

class Map extends Component<Props> {
	map = null as google.maps.Map;
	mc = null as MarkerClusterer;
	mapRef = null;
	infoWindow: google.maps.InfoWindow = null;
	imagePath = process.env.PUBLIC_URL + "/";
	mapMarkers = {
		now: {
			url: this.imagePath + "icon_32x40_pin_now.svg",
			labelOrigin: new google.maps.Point(16, 16),
		},
		later: {
			url: this.imagePath + "icon_32x40_pin_later.svg",
			labelOrigin: new google.maps.Point(16, 16),
		},
		earlier: {
			url: this.imagePath + "icon_32x40_pin_later.svg",
			labelOrigin: new google.maps.Point(16, 16),
		},
		noSlot: {
			url: this.imagePath + "icon_32x40_pin_noSlot.svg",
			labelOrigin: new google.maps.Point(16, 16),
		},
	};
	constructor(props) {
		super(props);

		this.mapRef = React.createRef();

		this.state = {};
	}

	componentDidMount() {
		this.initializeMap();
	}

	componentWillUnmount() {
		google.maps.event.clearInstanceListeners(this.map);
		this.map = null;
	}

	moveCenter = (center: google.maps.LatLng) => {
		if (this.map) {
			this.map.setCenter(center);
		}
	};

	getCenter = () => {
		return this.map.getCenter();
	};

	getRange = () => {
		const bounds = this.map.getBounds();
		if (!bounds) {
			return;
		}
		const ne = bounds.getNorthEast();
		const sw = bounds.getSouthWest();

		const distance = google.maps.geometry.spherical.computeDistanceBetween(sw, ne);
		return Math.ceil(distance / 2 / 1000);
	};

	expandMapControl = (controlDiv: HTMLDivElement) => {
		const controlUI = document.createElement("div");
		controlUI.setAttribute("id", "expandMap");

		const controlIcon = document.createElement("div");
		controlIcon.setAttribute("class", "icon");

		controlUI.appendChild(controlIcon);
		controlDiv.appendChild(controlUI);

		controlUI.addEventListener("click", function() {
			document.body.querySelector(".courtsPage").classList.toggle("mapExpanded");
		});
	};

	searchThisAreaControl = (controlDiv: HTMLDivElement) => {
		const controlUI = document.createElement("div");
		controlUI.setAttribute("id", "searchThisArea");

		const controlText = document.createElement("div");
		controlText.innerHTML = _("courts.map.searchThisArea");

		controlUI.appendChild(controlText);
		controlDiv.appendChild(controlUI);

		controlUI.addEventListener("click", () => {
			this.props.onMapMove(this);
		});
	};

	initializeMap = () => {
		const map = new google.maps.Map(this.mapRef.current, {
			center: this.props.center,
			zoom: 13,
			minZoom: 8,
			zoomControl: true,
			mapTypeControl: false,
			scaleControl: false,
			streetViewControl: false,
			rotateControl: false,
			fullscreenControl: false,
			styles: mapStyles,
		});

		this.map = map;

		this.mc = new MarkerClusterer(map, [], {
			gridSize: 40,
			zoomOnClick: true,
			minimumClusterSize: 2,
			maxZoom: 20,
			styles: [
				MarkerClusterer.withDefaultStyle(defaultClusterStyle),
				MarkerClusterer.withDefaultStyle({ ...defaultClusterStyle, textColor: "#7AC42B" }),
				MarkerClusterer.withDefaultStyle({ ...defaultClusterStyle, textColor: "#43B4DC" }),
				MarkerClusterer.withDefaultStyle({ ...defaultClusterStyle, textColor: "#40AED6" }),
			],
			calculator: (markers: google.maps.Marker[], clusterIconStylesCount: number): ClusterIconInfo => {
				const index = getClusterStyle(markers) + 1;

				return {
					title: undefined,
					text: markers.length.toString(),
					index,
				};
			},
		});

		const searchThisAreaDiv = document.createElement("div");
		this.searchThisAreaControl(searchThisAreaDiv);
		this.map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(searchThisAreaDiv);

		const expandMapDiv = document.createElement("div");
		this.expandMapControl(expandMapDiv);
		this.map.controls[google.maps.ControlPosition.TOP_LEFT].push(expandMapDiv);
	};

	clearMarkers = () => {
		this.mc.clearMarkers();
	};

	componentDidUpdate() {
		this.clearMarkers();

		this.props.results.forEach((result: SearchResult, index: number) => {
			const marker = new google.maps.Marker({
				icon: this.mapMarkers[result.className],
				position: { lat: parseFloat(result.latitude), lng: parseFloat(result.longitude) },
				label: { color: "#fff", fontSize: "12px", fontWeight: "600", text: (index + 1).toString() },
			});

			marker["className"] = result.className;
			this.mc.addMarker(marker);

			marker.addListener("click", () => {
				this.addInfoWindow(marker, result);
			});
		});
	}

	createInfoWindow = (result: SearchResult) => {
		let availability = <div className="noSlots">{_("courts.results.availability.noSlots")}</div>;
		if (result.nearestSlot) {
			const slot = result.nearestSlot;
			const time = moment.unix(slot.startDateTime).tz(result.timeZone);
			availability = (
				<div className={result.className}>
					{_("courts.results.availability", slot.numberOfCourts, {
						numberOfCourts: slot.numberOfCourts,
						time: time.format("HH:mm"),
					})}
				</div>
			);
		} else {
			if (result.type === "private") {
				availability = <div className="private">{_("courts.results.availability.private")}</div>;
			} else if (result.contractedClub === false) {
				availability = <div className="notContracted">{_("courts.results.availability.notContracted")}</div>;
			}
		}

		return (
			<a
				href={`${process.env.REACT_APP_BASE_URL}/club/${result.clubId}?timestamp=${this.props.timestamp}`}
				className="cell"
				target="_blank"
				rel="noopener noreferrer"
			>
				<div className="item club small">
					<div className="itemImage">
						<div
							className="avatar club"
							style={{ backgroundImage: `url(${result.photoUrl.replace("_DIMENSION_", "thumb3")})` }}
						/>
					</div>
					<div className="itemBody">
						<div className="title">
							<div className="longTextWrap">
								<div className="longText">{result.clubName}</div>
							</div>
						</div>
						<div className="availability">{availability}</div>
					</div>
				</div>
			</a>
		);
	};

	addInfoWindow = (marker: google.maps.Marker, result: SearchResult) => {
		if (this.infoWindow === null) {
			this.infoWindow = new google.maps.InfoWindow();
		} else {
			this.infoWindow.close();
		}

		const el = document.createElement("div");
		const window = ReactDOM.render(this.createInfoWindow(result), el);

		//@ts-ignore
		this.infoWindow.setContent(window.outerHTML);

		//@ts-ignore
		window.remove();
		el.remove();

		this.infoWindow.open(this.map, marker);
	};

	render() {
		return <div ref={this.mapRef} id="map" />;
	}
}

export default Map;
